import { Outlet, Link, useNavigate } from "react-router";
import { useAuth } from "../lib/auth-context";
import { useAuthStore } from "../lib/store";
import { usePermissions } from "../lib/permissions";
import { useState, useEffect, useCallback } from "react";
import * as api from "../lib/api";
import { startDesktopNotifications, stopDesktopNotifications } from "../lib/notifications";
import { Select, Tooltip, ThemeToggle } from "@mieweb/ui";
import { UserAvatar } from "./UserAvatar";
import { BrandLockup } from "./BrandMark";
import { UpdateBadge } from "./UpdateBadge";

export function AppLayout() {
  const {
    user,
    currentOrganization,
    currentBrand,
    brands,
    organizations,
    setCurrentOrganization,
    setCurrentBrand,
    setBrands,
    logout,
  } = useAuth();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const stored = localStorage.getItem("sidebar-open");
    return stored !== null ? stored === "true" : true;
  });
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Persist sidebar state
  useEffect(() => {
    localStorage.setItem("sidebar-open", String(isSidebarOpen));
  }, [isSidebarOpen]);

  // Desktop app: poll for new feedback and raise native notifications while
  // the authenticated shell is mounted (no-op on web).
  useEffect(() => {
    startDesktopNotifications();
    return () => stopDesktopNotifications();
  }, []);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setIsSidebarOpen(true); // Always show full sidebar content in mobile overlay
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Close mobile sidebar when navigating
  const closeMobileSidebar = useCallback(() => {
    if (isMobile) {
      setIsMobileSidebarOpen(false);
    }
  }, [isMobile]);

  // Load brands when organization changes, and restore persisted brand
  useEffect(() => {
    if (currentOrganization) {
      api
        .listBrands(currentOrganization.id)
        .then((loadedBrands) => {
          setBrands(loadedBrands);
          // Restore the persisted brand from the zustand store, or clear if stale
          const storeBrand = useAuthStore.getState().currentBrand;
          if (storeBrand && loadedBrands.length > 0) {
            const match = loadedBrands.find(
              (b: { id: string }) => b.id === storeBrand.id,
            );
            if (match) {
              setCurrentBrand(match);
            } else {
              // Stored brand doesn't exist in this org's brands — clear it
              setCurrentBrand(null);
            }
          } else if (storeBrand && loadedBrands.length === 0) {
            // No brands in this org — clear stale brand
            setCurrentBrand(null);
          }
        })
        .catch(console.error);
    } else {
      setBrands([]);
    }
  }, [currentOrganization, setBrands, setCurrentBrand]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navigation = [
    can("analytics.read") && {
      name: "Dashboard",
      href: "/dashboard",
      icon: HomeIcon,
    },
    can("feedback.read") && {
      name: "Board",
      href: "/board",
      icon: BoardIcon,
    },
    can("inboxes.read") && {
      name: "Inboxes",
      href: "/inboxes",
      icon: InboxIcon,
    },
    can("inboxes.read") && {
      name: "Feedback Pages",
      href: "/feedback-pages",
      icon: FeedbackPageIcon,
    },
    can("widgets.read") && {
      name: "Widgets",
      href: "/widgets",
      icon: WidgetIcon,
    },
    can("brands.read") && { name: "Brands", href: "/brands", icon: BrandIcon },
    can("org.read") && {
      name: "Settings",
      href: "/settings",
      icon: SettingsIcon,
    },
  ].filter(Boolean) as Array<{
    name: string;
    href: string;
    icon: React.ComponentType<{ className?: string }>;
  }>;

  const adminNavigation = user?.isSiteAdmin
    ? [{ name: "Site Admin", href: "/admin", icon: AdminIcon }]
    : [];

  // Sidebar content shared between desktop and mobile
  const sidebarContent = (
    <>
      {/* Organization Selector */}
      {isSidebarOpen && (
        <div className="shrink-0 border-b border-border p-4">
          <Select
            label="Organization"
            hideLabel={false}
            size="sm"
            placeholder="Select organization"
            value={currentOrganization?.id || ""}
            onValueChange={(value) => {
              const org = organizations.find((o) => o.id === value);
              setCurrentOrganization(org || null);
            }}
            options={organizations.map((org) => ({
              value: org.id,
              label: org.name,
            }))}
          />

          {brands.length > 0 && (
            <div className="mt-3">
              <Select
                label="Brand"
                hideLabel={false}
                size="sm"
                placeholder="All Brands"
                value={currentBrand?.id || ""}
                onValueChange={(value) => {
                  const brand = brands.find((b) => b.id === value);
                  setCurrentBrand(brand || null);
                }}
                options={[
                  { value: "", label: "All Brands" },
                  ...brands.map((brand) => ({
                    value: brand.id,
                    label: brand.name,
                  })),
                ]}
              />
            </div>
          )}
        </div>
      )}

      {/* Navigation */}
      <nav
        className={`flex-1 flex flex-col gap-1 overflow-y-auto ${isSidebarOpen ? "p-4" : "items-center py-4"}`}
      >
        {navigation.map((item) => {
          const linkContent = (
            <Link
              key={item.name}
              to={item.href}
              onClick={closeMobileSidebar}
              className={`flex items-center rounded-lg py-2 text-sm text-foreground hover:bg-surface-hover ${isSidebarOpen ? "gap-3 px-3" : "w-10 justify-center"}`}
            >
              <item.icon className="h-5 w-5 shrink-0" />
              {isSidebarOpen && <span>{item.name}</span>}
            </Link>
          );

          return isSidebarOpen ? (
            <div key={item.name}>{linkContent}</div>
          ) : (
            <Tooltip key={item.name} content={item.name} placement="right">
              {linkContent}
            </Tooltip>
          );
        })}

        {/* Admin Section */}
        {adminNavigation.length > 0 && (
          <>
            <div
              className={`my-2 border-t border-border ${isSidebarOpen ? "" : "w-8"}`}
            />
            {adminNavigation.map((item) => {
              const linkContent = (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={closeMobileSidebar}
                  className={`flex items-center rounded-lg py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 ${isSidebarOpen ? "gap-3 px-3" : "w-10 justify-center"}`}
                >
                  <item.icon className="h-5 w-5 shrink-0" />
                  {isSidebarOpen && <span>{item.name}</span>}
                </Link>
              );

              return isSidebarOpen ? (
                <div key={item.name}>{linkContent}</div>
              ) : (
                <Tooltip key={item.name} content={item.name} placement="right">
                  {linkContent}
                </Tooltip>
              );
            })}
          </>
        )}
      </nav>

      {/* Update badge — lights up while a deploy rolls out / when an update is ready */}
      <UpdateBadge collapsed={!isSidebarOpen} />

      {/* Theme Toggle */}
      <div
        className={`shrink-0 border-t border-border ${isSidebarOpen ? "px-4 py-2" : "flex justify-center py-2"}`}
      >
        {isSidebarOpen ? (
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Theme</span>
            <ThemeToggle mode="three-way" size="sm" variant="ghost" />
          </div>
        ) : (
          <ThemeToggle mode="three-way" size="sm" variant="ghost" />
        )}
      </div>

      {/* User Menu */}
      <div
        className={`shrink-0 border-t border-border ${isSidebarOpen ? "p-4" : "flex justify-center py-4"}`}
      >
        {isSidebarOpen ? (
          <div className="flex items-center gap-3">
            <Link
              to="/profile"
              onClick={closeMobileSidebar}
              className="flex flex-1 items-center gap-3 rounded-lg p-1 -m-1 hover:bg-surface-hover transition-colors min-w-0"
            >
              <UserAvatar email={user?.email} name={user?.name} avatarUrl={user?.avatarUrl} className="h-8 w-8 text-sm" />
              <div className="flex-1 min-w-0">
                <p className="truncate text-sm font-medium">
                  {user?.name || "User"}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {user?.email}
                </p>
              </div>
            </Link>
            <button
              onClick={handleLogout}
              className="shrink-0 text-muted-foreground hover:text-foreground"
            >
              <LogoutIcon className="h-5 w-5" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <Tooltip content="Profile" placement="right">
              <Link
                to="/profile"
                onClick={closeMobileSidebar}
                className="flex w-full justify-center text-muted-foreground hover:text-foreground"
              >
                <UserAvatar email={user?.email} name={user?.name} avatarUrl={user?.avatarUrl} className="h-8 w-8 text-sm" />
              </Link>
            </Tooltip>
            <Tooltip content="Logout" placement="right">
              <button
                onClick={handleLogout}
                className="flex w-full justify-center text-muted-foreground hover:text-foreground"
              >
                <LogoutIcon className="h-5 w-5" />
              </button>
            </Tooltip>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-surface">
      {/* Mobile Header */}
      {isMobile && (
        <header className="fixed top-0 left-0 right-0 z-40 flex h-14 items-center justify-between border-b border-border bg-surface-raised px-4">
          <BrandLockup
            className="text-lg font-bold text-primary"
            iconClassName="mr-1 h-5 w-5"
          />
          <button
            onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
            className="rounded p-2 text-foreground hover:bg-surface-hover"
            aria-label="Toggle menu"
          >
            {isMobileSidebarOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </header>
      )}

      {/* Mobile Sidebar Overlay */}
      {isMobile && isMobileSidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          ${
            isMobile
              ? `fixed top-14 left-0 bottom-0 z-40 w-64 transform transition-transform duration-200 ${isMobileSidebarOpen ? "translate-x-0" : "-translate-x-full"}`
              : `${isSidebarOpen ? "w-64" : "w-16"} transition-all duration-200`
          }
          flex flex-col border-r border-border bg-surface-raised
        `}
      >
        {/* Logo - Desktop only */}
        {!isMobile && (
          <div
            className={`flex h-16 shrink-0 items-center border-b border-border ${isSidebarOpen ? "justify-between px-4" : "justify-center"}`}
          >
            {isSidebarOpen && (
              <BrandLockup
                className="text-xl font-bold text-primary"
                iconClassName="mr-1 h-6 w-6"
              />
            )}
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="shrink-0 rounded p-1 text-muted-foreground hover:bg-surface-hover hover:text-foreground"
            >
              <ChevronIcon direction={isSidebarOpen ? "left" : "right"} />
            </button>
          </div>
        )}

        {sidebarContent}
      </aside>

      {/* Main Content */}
      <main className={`flex-1 overflow-auto ${isMobile ? "pt-14" : ""}`}>
        <Outlet />
      </main>
    </div>
  );
}

// Simple icon components
function HomeIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
      />
    </svg>
  );
}

function BoardIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"
      />
    </svg>
  );
}

function InboxIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
      />
    </svg>
  );
}

function FeedbackPageIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

function WidgetIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
      />
    </svg>
  );
}

function BrandIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>
  );
}

function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

function LogoutIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
      />
    </svg>
  );
}

function ChevronIcon({
  direction,
  className,
}: {
  direction: "left" | "right";
  className?: string;
}) {
  return (
    <svg
      className={className || "h-5 w-5"}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      {direction === "left" ? (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 19l-7-7 7-7"
        />
      ) : (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 5l7 7-7 7"
        />
      )}
    </svg>
  );
}

function MenuIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className || "h-6 w-6"}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 6h16M4 12h16M4 18h16"
      />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className || "h-6 w-6"}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

function AdminIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
      />
    </svg>
  );
}

